import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiUserCheck } from 'react-icons/fi';
import { authAPI, setTokens } from '../services/api';
import { fetchCurrentUser } from '../store/slices/authSlice';

/**
 * Opened in a new tab when a delegate picks one of their delegations from
 * "Bana Verilen Vekaletler". Mirrors ImpersonationHandoff: consume the
 * one-time token, keep the resulting session in sessionStorage so the
 * delegate's own session in the original tab is untouched, then enter the
 * delegator's workspace.
 */
function DelegationHandoff() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState(null);
  // Guards React 18 StrictMode's double-invoke in dev — the backend burns the
  // handoff token on the first call and the second would fail as "already used".
  const consumedRef = useRef(false);

  useEffect(() => {
    const handoffToken = params.get('token');
    if (!handoffToken) {
      setError(t('delegation.missingToken'));
      return;
    }
    if (consumedRef.current) return;
    consumedRef.current = true;

    // Keep the token out of the visible URL and browser history.
    try {
      window.history.replaceState({}, '', '/delegation/handoff');
    } catch { /* ignore — non-fatal */ }

    (async () => {
      try {
        const res = await authAPI.consumeDelegation(handoffToken);
        setTokens(
          { accessToken: res.data.accessToken, refreshToken: res.data.refreshToken },
          { delegation: true }
        );
        await dispatch(fetchCurrentUser()).unwrap();
        navigate('/dashboard', { replace: true });
      } catch (err) {
        setError(err.response?.data?.error || err.message || t('delegation.failed'));
      }
    })();
  }, [dispatch, navigate, params, t]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f8fafc',
      padding: '24px',
    }}>
      <div style={{
        maxWidth: 420,
        width: '100%',
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: '32px 28px',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          margin: '0 auto 16px',
          background: error ? '#fef2f2' : '#eef2ff',
          color: error ? '#ef4444' : '#6366f1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {error ? <FiAlertCircle size={24} /> : <FiUserCheck size={24} />}
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}>
          {error ? t('delegation.failedTitle') : t('delegation.entering')}
        </h2>
        <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem', lineHeight: 1.5 }}>
          {error || t('delegation.enteringDesc')}
        </p>
        {error && (
          <button
            type="button"
            onClick={() => window.close()}
            style={{
              marginTop: 20,
              padding: '9px 20px',
              borderRadius: 8,
              background: '#111827',
              color: '#fff',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('common.close')}
          </button>
        )}
      </div>
    </div>
  );
}

export default DelegationHandoff;
